import type { ICmsAhuDoc } from 'models';
import { BareSelect } from './formKit';
import { cmsSeasonsByComponent, type CmsNamedRow } from './cmsDoc';
import { SBareInput } from './component/formCommon';
import type { ComponentFormApi } from './useComponentForm';

/** Floor for every entering/leaving AIR temperature input, matching the
 * validators on AHUSection (ahu migration 0152) and the left edge of the
 * psychrometric chart's cold scale. A -40 °F winter design day is an
 * ordinary Canadian one; the old floor of 0 made it untypeable. */
export const MIN_AIR_TEMP_F = -40;

/**
 * Dev-sync J6 — the shared season-split kit (survey §C4 "Season shared kit").
 *
 * Legacy spec: eliminator/silencer form.tmpl season hunks (+296..372 /
 * +442..514, wave commit 16d5478c2b..origin/dev) and tab.section.tmpl
 * (+157..234). The Summer/Winter groups are DOUBLE-gated in legacy —
 * Handlebars (`{{#if section.season_id}}` + `{{#ifCompare section.season_id
 * '!=' 2}}`/`'!=' 1`) AND the merged `[data-season]` CSS
 * (django/static/css/ahu/styles.css:5898-5904). Per survey §C5 item 4 the
 * JSX gates the rows; the season-group-* classes + the container's
 * data-season attribute are kept so the merged CSS still applies.
 *
 * Season option lists render from the CMS payload's per-component rows
 * (`cms.air.season_by_component.<Component>`) — never hardcoded ids.
 */

/** The ifCompare gates: 1=Summer → summer only; 2=Winter → winter only;
 * 3 (Summer + Winter) → both; unset → neither. */
export function seasonShowFlags(seasonId: number | string | null | undefined): {
  showSummer: boolean;
  showWinter: boolean;
} {
  const id = Number(seasonId) || null;
  return { showSummer: id != null && id !== 2, showWinter: id != null && id !== 1 };
}

/**
 * The form-side Season row (plenum/eliminator/silencer grammar):
 *   <li><div><span class="unit"></span><span class="selection">…</span>
 *   <select class="section_field season_select" name="season_id">…</select>
 *   <span class="label">Season</span></div></li>
 * The eliminator tmpl also renders a duplicate `<option
 * value="{{section.season_id}}">{{section.season_value}}</option>` ahead of
 * the CMS rows — visible ONLY when the saved season is missing from the
 * component's allowed list (never for the seeded lists, where a later
 * `selected` option wins). Not replicated: BareSelect's controlled value
 * covers every reachable state.
 */
export const SeasonSelectRow = ({
  cms,
  component,
  value,
  onChange,
}: {
  cms: ICmsAhuDoc | undefined;
  component: string;
  value: number | string | null | undefined;
  onChange: (raw: string) => void;
}) => (
  <li>
    <div>
      <span className="unit" />{' '}
      <BareSelect
        name="season_id"
        fieldClass="section_field season_select"
        value={value}
        options={cmsSeasonsByComponent(cms, component) as CmsNamedRow[]}
        onChange={onChange}
      />{' '}
      <span className="label">Season</span>
    </div>
  </li>
);

/** `<li class="sub-heading season-group-<season>"><div><span
 * class="label">Summer</span></div></li>` */
export const SeasonHeadingRow = ({ season }: { season: 'summer' | 'winter' }) => (
  <li className={`sub-heading season-group-${season}`}>
    <div>
      <span className="label">{season === 'summer' ? 'Summer' : 'Winter'}</span>
    </div>
  </li>
);

/**
 * The eliminator/silencer Performance-tab season block: Season select +
 * per-season EDB|EWB (entering, `<type>-efficiency-mech`, max 150) and
 * LDB|LWB (the EDITABLE per-season leaving override, placeholder = that
 * season's entering — data-formatter_placeholder pattern). All eight inputs
 * are `.section_field` → api.commitSectionField (plain PATCH — these section
 * types are outside the plenum/evap_pack commit gate, section.js:200-284).
 *
 * leavingMax: eliminator tmpl uses data-max="240.00", silencer "150" —
 * passed per caller.
 */
export const ComponentSeasonBlock = ({
  api,
  section,
  cms,
  component,
  typePrefix,
  leavingMax,
}: {
  api: ComponentFormApi;
  section: Record<string, unknown>;
  cms: ICmsAhuDoc | undefined;
  component: string;
  typePrefix: string;
  leavingMax: number;
}) => {
  const seasonId = (section.season_id as number | null | undefined) ?? null;
  const { showSummer, showWinter } = seasonShowFlags(seasonId);

  const enteringPair = (season: 'summer' | 'winter') => (
    <li className={`season-group-${season}`}>
      <div className={`${typePrefix}-efficiency-mech`}>
        <span className="unit temp"> F </span>{' '}
        <SBareInput
          api={api}
          name={`eadb_temp_${season}`}
          value={section[`eadb_temp_${season}`]}
          min={MIN_AIR_TEMP_F}
          max={150}
        />{' '}
        <SBareInput
          api={api}
          name={`eawb_temp_${season}`}
          value={section[`eawb_temp_${season}`]}
          min={MIN_AIR_TEMP_F}
          max={150}
        />{' '}
        <span className="label">
          <span>EDB</span> | <span>EWB</span>
        </span>
      </div>
    </li>
  );

  const leavingPair = (season: 'summer' | 'winter') => (
    <li className={`season-group-${season}`}>
      <div>
        <span className="unit temp"> F </span>{' '}
        <SBareInput
          api={api}
          name={`ladb_temp_${season}`}
          value={section[`ladb_temp_${season}`]}
          min={MIN_AIR_TEMP_F}
          max={leavingMax}
          placeholder={section[`eadb_temp_${season}`]}
        />{' '}
        <SBareInput
          api={api}
          name={`lawb_temp_${season}`}
          value={section[`lawb_temp_${season}`]}
          min={MIN_AIR_TEMP_F}
          max={leavingMax}
          placeholder={section[`eawb_temp_${season}`]}
        />{' '}
        <span className="label">LDB | LWB</span>
      </div>
    </li>
  );

  return (
    <>
      <SeasonSelectRow
        cms={cms}
        component={component}
        value={seasonId}
        onChange={(raw) => api.commitSectionField('season_id', raw)}
      />
      {showSummer && (
        <>
          <SeasonHeadingRow season="summer" />
          {enteringPair('summer')}
          {leavingPair('summer')}
        </>
      )}
      {showWinter && (
        <>
          <SeasonHeadingRow season="winter" />
          {enteringPair('winter')}
          {leavingPair('winter')}
        </>
      )}
    </>
  );
};
